import { useMemo, useSyncExternalStore } from 'react';
import { requireNonNull } from 'utils/functions';
import logging from 'utils/logging';

export type Observer<T> = (t: T) => void;

export type Subscription = {
  unsubscribe(): void;
};

export class Dispatcher<T> {
  private readonly _listeners: Set<Observer<T>> = new Set();

  /**
   * Current value. Will be undefined when the initial value is not yet available.
   */
  current: T | undefined = undefined;

  private readonly supplier: (() => Promise<T>) | undefined;
  private promises: Promise<T> | undefined = undefined;
  private awaited = false;

  constructor(initialValue?: () => Promise<T>) {
    this.supplier = initialValue;
  }

  /**
   * Subscribe to updates
   */
  subscribe(observer: Observer<T>): Subscription {
    if (!this.awaited) {
      this.awaited = true;
      this.forceUpdate().catch((error) =>
        logging.error({
          message: 'exception while loading initial value',
          error,
        }),
      );
    }
    queueMicrotask(() => {
      this._listeners.add(observer);
      if (this.current !== undefined) observer(this.current);
    });
    return {
      unsubscribe: () => {
        this._listeners.delete(observer);
      },
    };
  }

  /**
   * Creates a promise that resolves with a single value.
   */
  promise(): Promise<T> {
    return new Promise((resolve) => {
      const subscription = this.subscribe((value) => {
        resolve(value);
        subscription.unsubscribe();
      });
    });
  }

  /**
   * Update with a new value
   */
  emit(t: T): void {
    queueMicrotask(() => {
      this.current = t;
      this._listeners.forEach((observer) => observer(t));
    });
  }

  forceUpdate(): Promise<T> {
    if (this.supplier == null) return Promise.reject();
    if (!this.promises) {
      this.promises = requireNonNull(this.supplier)();
      this.promises
        .then((value) => this.emit(value))
        .finally(() => (this.promises = undefined));
    }
    return this.promises;
  }
}

export type Dispatch<T> = {
  current: T | undefined;
  forceUpdate(): Promise<T>;
  promise(): Promise<T>;
};

export function useDispatcher<T>(observable: Dispatcher<T>): Dispatch<T> {
  const current = useSyncExternalStore(
    (notify) => {
      const subscription = observable.subscribe(notify);
      return () => subscription.unsubscribe();
    },
    () => observable.current,
    () => undefined,
  );
  return useMemo(() => {
    return {
      current,
      forceUpdate: () => observable.forceUpdate(),
      promise: () => observable.promise(),
    };
  }, [current, observable]);
}
